import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { FileSpreadsheet, Table, Upload } from "lucide-react";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import ErrorLogger from "@/utils/errorLogger";

export const DataImport = () => {
  const [isUploading, setIsUploading] = useState(false);
  const [isConnecting, setIsConnecting] = useState<string | null>(null);

  const { data: user } = useQuery({
    queryKey: ["user"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    },
  });

  const handleConnectGoogleSheets = async () => {
    try {
      setIsConnecting("google");
      console.log("Initiating Google Sheets connection through integration");
      
      // Instead of OAuth here, redirect to the Google Sheets Integration section
      const element = document.querySelector('[data-google-sheets-integration]');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        toast.success("Scroll down to the Google Sheets Integration section to connect");
      } else {
        toast.info("Use the Google Sheets Integration section below to connect your sheets");
      }
    } catch (error) {
      console.error("Error connecting to Google Sheets:", error);
      toast.error("Failed to connect to Google Sheets");
    } finally {
      setIsConnecting(null);
    }
  };

  const handleConnectExcel = async () => {
    try {
      setIsConnecting("excel");
      console.log("Initiating Excel Online OAuth flow");
      
      const redirectUri = encodeURIComponent(`${window.location.origin}/data?crm_type=excel_online&oauth_success=true`);
      
      toast.success("Excel Online connection initiated");
    } catch (error) {
      console.error("Error connecting to Excel Online:", error);
      toast.error("Failed to connect to Excel Online");
      ErrorLogger.logError(error instanceof Error ? error : new Error("Failed to connect to Excel Online"));
    } finally {
      setIsConnecting(null);
    }
  };

  const handleUploadFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel', // .xls
      'text/csv' // .csv
    ];

    if (!allowedTypes.includes(file.type) && !file.name.toLowerCase().endsWith('.csv')) {
      toast.error("Please upload only Excel (.xlsx, .xls) or CSV files");
      event.target.value = "";
      return;
    }

    // Check file size (limit to 50MB)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      toast.error("File size must be less than 50MB");
      event.target.value = "";
      return;
    }

    setIsUploading(true);
    console.log(`Starting upload for file: ${file.name}, size: ${file.size} bytes, type: ${file.type}`);

    try {
      if (!user) {
        throw new Error("You must be logged in to upload files");
      }

      // Create a unique file path with user ID and timestamp
      const timestamp = Date.now();
      const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const fileName = `${user.id}/${timestamp}_${sanitizedFileName}`;
      
      console.log(`Uploading to path: ${fileName}`);

      // Upload file to Supabase storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from("user_files")
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error("Upload error:", uploadError);
        throw new Error(`Upload failed: ${uploadError.message}`);
      }

      console.log("File uploaded successfully:", uploadData);

      // Get the public URL for the uploaded file
      const { data: { publicUrl } } = supabase.storage
        .from("user_files")
        .getPublicUrl(fileName);

      // Record the file upload in the database
      const { error: fileStorageError } = await supabase
        .from("file_storage")
        .insert({
          file_name: file.name,
          file_path: fileName,
          folder_path: `/${user.id}/`,
          size: file.size,
          mime_type: file.type || 'application/octet-stream',
          is_folder: false,
          user_id: user.id
        });

      if (fileStorageError) {
        console.error("File storage record error:", fileStorageError);
        // Don't fail the upload if we can't record it, just log it
        console.warn("File uploaded but failed to record in database:", fileStorageError.message);
      }

      // Record as a data source
      const { error: dataSourceError } = await supabase
        .from("data_sources")
        .insert([{
          user_id: user.id,
          name: file.name,
          source_type: file.name.toLowerCase().endsWith('.csv') ? 'csv_file' : 'excel_file',
          status: "Active",
        }]);

      if (dataSourceError) {
        console.error("Data source record error:", dataSourceError);
        // Don't fail the upload if we can't record it, just log it
        console.warn("File uploaded but failed to record as data source:", dataSourceError.message);
      }

      toast.success(`File "${file.name}" uploaded successfully! You can now use it for data analysis.`);
      
      // Dispatch event to refresh data sources
      window.dispatchEvent(new CustomEvent("data-source-updated"));
      
    } catch (error: any) {
      console.error("File upload failed:", error);
      const errorMessage = error.message || "Unknown error occurred during upload";
      toast.error(`Upload failed: ${errorMessage}`);
      ErrorLogger.logError(error instanceof Error ? error : new Error(errorMessage));
    } finally {
      setIsUploading(false);
      // Reset the file input
      event.target.value = "";
    }
  };

  return (
    <Card className="p-6">
      <h2 className="text-lg font-semibold mb-4">Import Data</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Button
          variant="outline"
          className="h-auto py-6 flex flex-col items-center gap-2"
          onClick={handleConnectGoogleSheets}
          disabled={isConnecting === "google"}
        >
          <Table className="h-8 w-8" />
          <span>{isConnecting === "google" ? "Connecting..." : "Connect Google Sheets"}</span>
        </Button>
        
        <Button
          variant="outline"
          className="h-auto py-6 flex flex-col items-center gap-2"
          onClick={handleConnectExcel}
          disabled={isConnecting === "excel"}
        >
          <FileSpreadsheet className="h-8 w-8" />
          <span>{isConnecting === "excel" ? "Connecting..." : "Connect Excel Online"}</span>
        </Button>
        
        <Button
          variant="outline"
          className="h-auto py-6 flex flex-col items-center gap-2"
          disabled={isUploading}
          asChild
        >
          <label className="cursor-pointer">
            <input
              type="file"
              className="hidden"
              accept=".xlsx,.xls,.csv"
              onChange={handleUploadFile}
              disabled={isUploading}
            />
            <Upload className="h-8 w-8" />
            <span>{isUploading ? "Uploading..." : "Upload Excel/CSV File"}</span>
          </label>
        </Button>
      </div>
      {isUploading && (
        <div className="mt-4 text-center text-sm text-muted-foreground">
          Uploading file... Please wait.
        </div>
      )}
    </Card>
  );
};
